/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Text } from '@fluentui/react/lib/Text';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { IntentsList } from 'src/luis/projects/analytics/components/OverallPredictionsCard/ProblemsSection/IntentsList';
import { InformationCard } from 'src/luis/projects/analytics/components/TrainingEvaluation/InformationCard';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { LuisId } from 'src/luis/types/Resource';

export type DataImbalanceProblemProps = {
    /**
     * The needy intents if exist.
     */
    needyIntents: IntentMetadata[];

    /**
     * The greedy intents if exist.
     */
    greedyIntents: IntentMetadata[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId) => void;
};

const messages = defineMessages({
    imbalanceProblemTitle: {
        id: 'analytics.overallPredictionsCard.imbalanceProblemTitle',
        defaultMessage: 'Data Imbalance'
    },
    needyIntentsDescription: {
        id: 'analytics.overallPredictionsCard.needyIntentsDescription',
        defaultMessage: `These intents had many fewer utterances than other intents in your app,
            which can weigh predictions away from this intent. Consider adding more utterances to those intents.`
    },
    greedyIntentsDescription: {
        id: 'analytics.overallPredictionsCard.greedyIntentsDescription',
        defaultMessage: `These intents dominate the utterances which can weigh predictions
            erroneously towards those intents. Consider redistributing utterances on those intents.`
    }
});

/**
 * Represents the data imbalance problem card.
 */
export const DataImbalanceProblem = (props: DataImbalanceProblemProps) => {
    const { needyIntents, greedyIntents, onIntentClick } = props;
    const { localize } = useLocalization();

    return (
        <InformationCard title={localize(messages.imbalanceProblemTitle)}>
            {Boolean(needyIntents.length) && <Text>{localize(messages.needyIntentsDescription)}</Text>}
            <IntentsList intents={needyIntents.slice(0, 3)} onIntentClick={onIntentClick} />
            {Boolean(greedyIntents.length) && <Text>{localize(messages.greedyIntentsDescription)}</Text>}
            <IntentsList intents={greedyIntents.slice(0, 3)} onIntentClick={onIntentClick} />
        </InformationCard>
    );
};
